'use client'

import { useMemo, useState } from 'react'
import dynamic from 'next/dynamic'
import { motion, AnimatePresence } from '@/lib/framer-motion-shim'
import ProjectCard from '@/components/ProjectCard'
import { useI18n } from '@/lib/i18n/context'
import type { DonneesCatalogue } from './catalogue-projets'
import { Pictogram } from '@/components/Pictogram'
import VagueBanniere from '@/components/VagueBanniere'

/**
 * Ce composant porte `'use client'` : tout ce qu'il importe part dans le
 * paquet du navigateur. Il importait real-projects.json (1,5 Mo) et, via
 * `getProjectImage`/`ProjectCard`, project-photos.json et project-scores.json
 * (1 Mo) — 3 230 586 octets de JavaScript décompressé pour cette page
 * (audit performance A1, 20 septembre 2026).
 *
 * Les filtres, les compteurs, la carte et la pagination portent sur
 * l'ensemble du portefeuille et doivent rester côté navigateur : la liste
 * continue donc d'y arriver, mais réduite aux champs affichés et préparée par
 * `catalogue-projets.ts`. Ne rien réimporter de `@/data/*` ici.
 *
 * `FranceMap` (Leaflet + MarkerCluster, chargés par `chargerLeaflet`, voir
 * `src/lib/leaflet-chargeur.ts`) est visible dès l'arrivée sur la page — elle
 * reste montée immédiatement (pas de `LazyMount`, contrairement aux cartes
 * hors écran corrigées ailleurs dans ce PR) — mais son code ne doit pas
 * grossir le bundle principal de la page. `next/dynamic({ ssr: false })`
 * la sort du bundle initial et du rendu serveur ; le texte et les
 * cartes-projets ci-dessous restent, eux, rendus côté serveur normalement.
 * Le squelette a la même hauteur que la carte réelle (`h-[500px]
 * lg:h-[620px]`) pour ne provoquer aucun saut de mise en page (CLS) à
 * l'hydratation.
 */
const FranceMap = dynamic(() => import('@/components/FranceMap'), {
  ssr: false,
  loading: () => (
    <div
      className="h-[500px] lg:h-[620px] w-full rounded-2xl bg-sage/40 animate-pulse"
      aria-hidden="true"
    />
  ),
})

const PAGE_SIZE = 18
const ALL_REGIONS = '__all__'

export default function ProjectsCatalogue({ donnees }: { donnees: DonneesCatalogue }) {
  const { projets: PROJECTS, regions: REGIONS, types: TYPES, totalProjects, regionsCovered } = donnees
  const { t, locale } = useI18n()
  const isFr = locale !== 'en'
  const [activeType, setActiveType] = useState<string>('all')
  const [activeRegion, setActiveRegion] = useState<string>(ALL_REGIONS)
  const [visible, setVisible] = useState(PAGE_SIZE)
  const [filtersExpanded, setFiltersExpanded] = useState(false)

  const filtered = useMemo(() => {
    return PROJECTS.filter((p) => {
      if (activeType !== 'all' && p.type !== activeType) return false
      if (activeRegion !== ALL_REGIONS && p.region !== activeRegion) return false
      return true
    })
  }, [PROJECTS, activeType, activeRegion])

  const resetAndSet = (fn: () => void) => {
    fn()
    setVisible(PAGE_SIZE)
  }

  const clearAllFilters = () => {
    setActiveType('all')
    setActiveRegion(ALL_REGIONS)
    setVisible(PAGE_SIZE)
  }

  const hasActiveFilters =
    activeType !== 'all' ||
    activeRegion !== ALL_REGIONS

  const activeFilterCount = [
    activeType !== 'all',
    activeRegion !== ALL_REGIONS,
  ].filter(Boolean).length

  const displayed = filtered.slice(0, visible)
  const totalCO2 = filtered.reduce((s, p) => s + p.co2Total, 0)
  const totalSurface = filtered.reduce((s, p) => s + p.surface, 0)
  const numFmt = new Intl.NumberFormat(locale === 'en' ? 'en-US' : 'fr-FR')
  const labels = t.typeLabels as Record<string, string>

  const heroBadge =
    locale === 'en'
      ? `${totalProjects} Label Bas-Carbone approved projects`
      : `${totalProjects} projets labellisés Label Bas-Carbone`

  const heroSubtitle =
    locale === 'en'
      ? `Explore ${totalProjects} agricultural and forestry projects spread across ${regionsCovered} French regions.`
      : `Explorez ${totalProjects} projets agricoles et forestiers répartis dans ${regionsCovered} régions françaises.`

  const loadMoreLabel = (n: number) =>
    locale === 'en' ? `See ${n} more projects` : `Voir ${n} projets de plus`
  const restLabel = (rest: number) =>
    locale === 'en' ? `(${rest} remaining)` : `(${rest} restants)`

  // Region for map zoom — null when "all"
  const mapRegion = activeRegion === ALL_REGIONS ? null : activeRegion


  return (
    <>
      {/* Hero */}
      <section className="gradient-forest pt-32 pb-16 md:pb-20 relative">
        <div className="container-site relative z-10 text-center">
          <span className="inline-block bg-white/10 text-amber font-heading font-semibold text-xs uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
            {heroBadge}
          </span>
          <h1 className="font-heading font-extrabold titre-banniere text-white">
            {t.projets.title}
          </h1>
          <p className="mt-4 text-lg text-white/70 max-w-2xl mx-auto">{heroSubtitle}</p>
        </div>
        <VagueBanniere />
      </section>

      {/* Map + Filters — side by side on desktop */}
      <section className="section-padding bg-cream pb-4">
        <div className="container-site">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
            {/* Left: France Map — 3/5 of the width */}
            <div className="lg:col-span-3 lg:sticky lg:top-24">
              <FranceMap projects={filtered} height="h-[500px] lg:h-[620px]" activeRegion={mapRegion} />
            </div>

            {/* Right: Filters + Stats — 2/5 of the width */}
            <div className="lg:col-span-2 flex flex-col gap-6">
              {/* Filters Header with collapse button on mobile */}
              <div className="lg:hidden flex items-center justify-between gap-4 pb-2 border-b border-gray-200">
                <h2 className="text-sm font-semibold text-gray-900">
                  {isFr ? 'Filtres avancés' : 'Advanced Filters'}
                  {hasActiveFilters && (
                    <span className="ml-2 inline-block bg-amber text-white text-xs font-bold px-2 py-1 rounded-full">
                      {activeFilterCount}
                    </span>
                  )}
                </h2>
                <button
                  onClick={() => setFiltersExpanded(!filtersExpanded)}
                  className="text-forest font-semibold text-sm"
                  aria-label={isFr ? 'Basculer les filtres' : 'Toggle filters'}
                >
                  {filtersExpanded ? '▼' : '▶'}
                </button>
              </div>

              {/* Desktop Filters (always visible) */}
              <div className="hidden lg:flex lg:flex-col lg:gap-6">
                {/* Region filter */}
                <div>
                  <label className="block text-xs text-gray-500 uppercase tracking-wider font-semibold mb-2">
                    {isFr ? 'Région' : 'Region'}
                  </label>
                  <select
                    value={activeRegion}
                    onChange={(e) => resetAndSet(() => setActiveRegion(e.target.value))}
                    className="w-full px-4 py-3 rounded-xl bg-white border border-gray-200 text-sm text-gray-700 focus:outline-none focus:border-forest focus:ring-2 focus:ring-forest/10 transition"
                  >
                    <option value={ALL_REGIONS}>{t.projets.allRegions}</option>
                    {REGIONS.map((region) => (
                      <option key={region} value={region}>
                        {region}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Type filters */}
                <div>
                  <label className="block text-xs text-gray-500 uppercase tracking-wider font-semibold mb-2">
                    {isFr ? 'Méthode' : 'Method'}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => resetAndSet(() => setActiveType('all'))}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                        activeType === 'all'
                          ? 'bg-forest text-white'
                          : 'bg-white text-gray-600 hover:bg-sage border border-gray-200'
                      }`}
                    >
                      {t.projets.allMethods}
                    </button>
                    {TYPES.map((filter) => (
                      <button
                        key={filter.value}
                        onClick={() => resetAndSet(() => setActiveType(filter.value))}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                          activeType === filter.value
                            ? 'bg-forest text-white'
                            : 'bg-white text-gray-600 hover:bg-sage border border-gray-200'
                        }`}
                      >
                        {labels[filter.value] || filter.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Clear all filters button */}
                {hasActiveFilters && (
                  <button
                    onClick={clearAllFilters}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-gray-700 text-sm font-semibold hover:bg-gray-50 transition-colors"
                  >
                    {isFr ? 'Réinitialiser tous les filtres' : 'Clear all filters'}
                  </button>
                )}
              </div>

              {/* Mobile Filters (collapsible) */}
              <AnimatePresence>
                {filtersExpanded && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="lg:hidden flex flex-col gap-6"
                  >
                    {/* Region filter */}
                    <div>
                      <label className="block text-xs text-gray-500 uppercase tracking-wider font-semibold mb-2">
                        {isFr ? 'Région' : 'Region'}
                      </label>
                      <select
                        value={activeRegion}
                        onChange={(e) => resetAndSet(() => setActiveRegion(e.target.value))}
                        className="w-full px-4 py-3 rounded-xl bg-white border border-gray-200 text-sm text-gray-700 focus:outline-none focus:border-forest focus:ring-2 focus:ring-forest/10 transition"
                      >
                        <option value={ALL_REGIONS}>{t.projets.allRegions}</option>
                        {REGIONS.map((region) => (
                          <option key={region} value={region}>
                            {region}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Type filters */}
                    <div>
                      <label className="block text-xs text-gray-500 uppercase tracking-wider font-semibold mb-2">
                        {isFr ? 'Méthode' : 'Method'}
                      </label>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => resetAndSet(() => setActiveType('all'))}
                          className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                            activeType === 'all'
                              ? 'bg-forest text-white'
                              : 'bg-white text-gray-600 hover:bg-sage border border-gray-200'
                          }`}
                        >
                          {t.projets.allMethods}
                        </button>
                        {TYPES.map((filter) => (
                          <button
                            key={filter.value}
                            onClick={() => resetAndSet(() => setActiveType(filter.value))}
                            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                              activeType === filter.value
                                ? 'bg-forest text-white'
                                : 'bg-white text-gray-600 hover:bg-sage border border-gray-200'
                            }`}
                          >
                            {labels[filter.value] || filter.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Clear all filters button */}
                    {hasActiveFilters && (
                      <button
                        onClick={clearAllFilters}
                        className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-gray-700 text-sm font-semibold hover:bg-gray-50 transition-colors"
                      >
                        {isFr ? 'Réinitialiser tous les filtres' : 'Clear all filters'}
                      </button>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Active filters summary */}
              {hasActiveFilters && (
                <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-gray-200">
                  {activeRegion !== ALL_REGIONS && (
                    <span className="inline-flex items-center gap-1.5 bg-forest/10 text-forest text-xs font-medium px-3 py-1.5 rounded-full">
                      <Pictogram name="map-pin" className="w-3.5 h-3.5" />
                      {activeRegion}
                      <button
                        onClick={() => resetAndSet(() => setActiveRegion(ALL_REGIONS))}
                        className="hover:text-red-500 transition-colors ml-1"
                        aria-label={isFr ? 'Supprimer le filtre région' : 'Remove region filter'}
                      >
                        &times;
                      </button>
                    </span>
                  )}
                  {activeType !== 'all' && (
                    <span className="inline-flex items-center gap-1.5 bg-forest/10 text-forest text-xs font-medium px-3 py-1.5 rounded-full">
                      {labels[activeType] || activeType}
                      <button
                        onClick={() => resetAndSet(() => setActiveType('all'))}
                        className="hover:text-red-500 transition-colors ml-1"
                        aria-label={isFr ? 'Supprimer le filtre méthode' : 'Remove method filter'}
                      >
                        &times;
                      </button>
                    </span>
                  )}
                </div>
              )}

              {/* Stats cards */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-white rounded-xl p-4 border border-gray-100 text-center">
                  <p className="text-2xl font-heading font-bold text-forest">{filtered.length}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{t.projets.found}</p>
                </div>
                <div className="bg-white rounded-xl p-4 border border-gray-100 text-center">
                  <p className="text-2xl font-heading font-bold text-forest">{numFmt.format(Math.round(totalSurface))}</p>
                  <p className="text-xs text-gray-500 mt-0.5">hectares</p>
                </div>
                <div className="bg-white rounded-xl p-4 border border-gray-100 text-center">
                  <p className="text-2xl font-heading font-bold text-forest">{numFmt.format(Math.round(totalCO2))}</p>
                  <p className="text-xs text-gray-500 mt-0.5">tCO₂</p>
                </div>
              </div>

              {/* Mini legend */}
              <p className="text-xs text-gray-500 leading-relaxed">
                {locale === 'en'
                  ? 'Click on a department bubble on the map to see local projects. Use the filters above to narrow down results.'
                  : 'Cliquez sur une bulle de département sur la carte pour voir les projets locaux. Utilisez les filtres ci-dessus pour affiner les résultats.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Project Grid */}
      <section className="pb-24 bg-cream">
        <div className="container-site">
          <AnimatePresence mode="wait">
            <motion.div
              key={`${activeType}-${activeRegion}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            >
              {displayed.map((p, i) => {
                const loc = p.commune ?? p.departement ?? p.region
                const presentation =
                  locale === 'en'
                    ? `${p.methode} project on ${numFmt.format(p.surface)} ha in ${loc}. ${numFmt.format(Math.round(p.co2Total))} tonnes of CO₂ stored.`
                    : `Projet ${p.methode.toLowerCase()} sur ${numFmt.format(p.surface)} ha à ${loc}. ${numFmt.format(Math.round(p.co2Total))} tonnes de CO₂ stockées.`
                return (
                  <ProjectCard
                    key={p.slug}
                    title={p.title}
                    slug={p.slug}
                    type={p.type}
                    presentation={presentation}
                    region={p.region}
                    commune={p.commune ?? undefined}
                    surface={p.surface}
                    co2Tonnes={Math.round(p.co2Total)}
                    featuredImage={p.coverImage ?? undefined}
                    impactAverage={p.impactAverage}
                    index={i}
                  />
                )
              })}
            </motion.div>
          </AnimatePresence>

          {filtered.length === 0 && (
            <div className="text-center py-20">
              <Pictogram name="search" className="w-10 h-10 mb-4 mx-auto text-gray-400" />
              <p className="font-heading font-semibold text-gray-900 text-lg">
                {t.projets.noResults}
              </p>
              <p className="text-gray-500 text-sm mt-2">{t.projets.noResultsHint}</p>
            </div>
          )}

          {/* Load more */}
          {visible < filtered.length && (
            <div className="text-center mt-10">
              <button
                onClick={() => setVisible((v) => v + PAGE_SIZE)}
                className="px-6 py-3 rounded-full bg-forest text-white font-medium hover:bg-forest-light transition-colors"
              >
                {loadMoreLabel(Math.min(PAGE_SIZE, filtered.length - visible))}
                <span className="opacity-70 ml-2">{restLabel(filtered.length - visible)}</span>
              </button>
            </div>
          )}
        </div>
      </section>
    </>
  )
}
